import type {
  EmployerFootprintAdminViewDto,
  EmployerVerificationDetail,
} from '../../api/employerVerificationDashboardApi';

const GENERIC_PLACE_TYPES = new Set([
  'point_of_interest',
  'establishment',
  'premise',
  'geocode',
]);

/** Internal coarse DB sectors — prefer footprint / Places / AI narrative over these. */
const COARSE_EMPLOYER_SECTORS = new Set([
  'IT',
  'SERVICES',
  'UNKNOWN',
  'OTHER',
  'RETAIL',
  'FINANCE',
  'MANUFACTURING',
  'HEALTH',
]);

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function readString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function humanizeToken(value: string): string {
  return value
    .split(/[_/\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function humanizePlaceTypes(types: unknown): string | null {
  if (!Array.isArray(types)) return null;
  const meaningful = types
    .map((t) => (typeof t === 'string' ? t.trim().toLowerCase() : ''))
    .filter((t) => t && !GENERIC_PLACE_TYPES.has(t));
  if (!meaningful.length) return null;
  return humanizeToken(meaningful[0]!);
}

function isCoarseEmployerSector(value: string): boolean {
  const normalized = value.trim().toUpperCase();
  if (COARSE_EMPLOYER_SECTORS.has(normalized)) return true;
  return normalized.length <= 3;
}

function sectorFromNarrative(text: string | null | undefined): string | null {
  if (!text?.trim()) return null;

  const companyMatch = text.match(
    /(?:which is a|is a|as a)\s+(?:large\s+)?([^.,"]+?)\s+company/i,
  );
  if (companyMatch?.[1]) {
    const sector = companyMatch[1].trim();
    if (sector && !isCoarseEmployerSector(sector)) {
      return humanizeToken(sector.replace(/\s+/g, ' '));
    }
  }

  const powerMatch = text.match(
    /\b(electricity generation|power generation|electric utilities?|electric utility)\b/i,
  );
  if (powerMatch?.[1]) {
    return humanizeToken(powerMatch[1]);
  }

  return null;
}

function structuralEvidence(evidence: Record<string, unknown> | null): Record<string, unknown> {
  if (!evidence) return {};
  const nested = evidence.gateEvidence;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const structural = (nested as Record<string, unknown>).STRUCTURAL_SANITY;
    if (structural && typeof structural === 'object' && !Array.isArray(structural)) {
      return { ...evidence, ...(structural as Record<string, unknown>) };
    }
  }
  const gateEv = evidence.STRUCTURAL_SANITY;
  if (gateEv && typeof gateEv === 'object' && !Array.isArray(gateEv)) {
    return { ...evidence, ...(gateEv as Record<string, unknown>) };
  }
  return evidence;
}

/** Best-effort workplace sector from footprint, Places, AI narrative, or structural evidence. */
export function readObservedEmployerSector(input: {
  detail?: EmployerVerificationDetail | null;
  footprint?: EmployerFootprintAdminViewDto | null;
  aiCreditMessage?: string | null;
  checkpointReason?: string | null;
}): string | null {
  const footprintSector = readString(input.footprint?.companyProfile?.sector);
  if (footprintSector) return footprintSector;

  const evidence = asRecord(input.detail?.evidence);
  const struct = structuralEvidence(evidence);
  const ai = asRecord(evidence?.aiRoleSectorAnalysis);

  const primaryBusiness = readString(struct.primaryBusinessType);
  if (primaryBusiness) return primaryBusiness;

  const fromAiCredit =
    sectorFromNarrative(input.aiCreditMessage) ??
    sectorFromNarrative(readString(ai?.creditAdjudicatorMessage));
  if (fromAiCredit) return fromAiCredit;

  const fromCheckpointReason = sectorFromNarrative(input.checkpointReason);
  if (fromCheckpointReason) return fromCheckpointReason;

  const inferredCategory = readString(struct.inferredPlaceCategory);
  if (inferredCategory && inferredCategory !== 'UNKNOWN' && inferredCategory !== 'GENERAL_BUSINESS') {
    return humanizeToken(inferredCategory);
  }

  const intel = asRecord(evidence?.intelligenceSnapshot);
  const fromIntel = humanizePlaceTypes(intel?.placeTypes);
  if (fromIntel) return fromIntel;

  const placeTypes = humanizePlaceTypes(struct.placeTypes);
  if (placeTypes) return placeTypes;

  const employerSector = readString(input.detail?.employer?.sector);
  if (employerSector && !isCoarseEmployerSector(employerSector)) {
    return employerSector;
  }

  return null;
}

/** Shared one-line issue copy for summary card + AI popover. */
export function buildSectorMismatchIssueStatement(input: {
  declaredIndustry: string | null;
  observedSector: string | null;
  employerName: string | null;
}): string {
  const { declaredIndustry, observedSector, employerName } = input;

  if (declaredIndustry && observedSector) {
    return `Customer declared "${declaredIndustry}" but the employer operates in "${observedSector}".`;
  }
  if (declaredIndustry && employerName) {
    return `Customer declared "${declaredIndustry}" but selected ${employerName}.`;
  }
  if (employerName && observedSector) {
    return `${employerName} operates in "${observedSector}" — check this matches what the customer declared.`;
  }
  return 'The declared industry and employer workplace type do not clearly align.';
}
